<template>
  <component
    :is="tag"
    class="base-btn"
    :class="[
      `base-btn--${variant}`,
      `base-btn--${size}`,
      {
        'base-btn--block': block,
        'base-btn--loading': loading,
        'base-btn--icon-only': isIconOnly,
      },
    ]"
    :type="tag === 'button' ? type : undefined"
    :to="isRouterLink ? to : undefined"
    :href="tag === 'a' ? href : undefined"
    :disabled="tag === 'button' ? (disabled || loading) : undefined"
    v-bind="$attrs"
  >
    <span v-if="loading" class="base-btn__spinner" aria-hidden="true" />
    <font-awesome-icon
      v-if="icon && !loading"
      :icon="icon"
      class="base-btn__icon"
    />
    <span v-if="hasLabel" class="base-btn__label">
      <slot />
    </span>
    <font-awesome-icon
      v-if="iconRight && !loading"
      :icon="iconRight"
      class="base-btn__icon base-btn__icon--right"
    />
  </component>
</template>

<script setup lang="ts">
import { computed, useSlots } from 'vue';
import { RouterLink } from 'vue-router';

interface Props {

  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'info' | 'ghost' | 'text';

  size?: 'sm' | 'md' | 'lg';

  icon?: string;

  iconRight?: string;

  loading?: boolean;

  disabled?: boolean;

  block?: boolean;

  type?: 'button' | 'submit' | 'reset';

  to?: string | Record<string, unknown>;

  href?: string;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'lg',
  loading: false,
  disabled: false,
  block: false,
  type: 'button',
});

const slots = useSlots();

const tag = computed<typeof RouterLink | 'a' | 'button'>(() => {
  if (props.to) return RouterLink;
  if (props.href) return 'a';
  return 'button';
});

const isRouterLink = computed(() => tag.value === RouterLink);

const hasLabel = computed(() => !!slots.default);
const isIconOnly = computed(() => !hasLabel.value && !!(props.icon || props.iconRight));
</script>

<style scoped>

.base-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-family: inherit;
  font-weight: 500;
  line-height: 1;
  cursor: pointer;
  border: none;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background 0.2s,
    box-shadow 0.2s,
    opacity 0.2s,
    filter 0.15s;
  -webkit-user-select: none;
  user-select: none;
}

.base-btn:focus-visible {
  outline: 2px solid #D8670C;
  outline-offset: 2px;
}

.base-btn--sm {
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  border-radius: 0.375rem;
}

.base-btn--md {
  padding: 0.6875rem 1.5rem;
  font-size: 0.9375rem;
  border-radius: 0.5rem;
}

.base-btn--lg {
  padding: 0.875rem 2rem;
  font-size: 1rem;
  border-radius: 0.5rem;
}

.base-btn--icon-only.base-btn--sm {
  padding: 0.375rem;
  width: 2rem;
  height: 2rem;
}
.base-btn--icon-only.base-btn--md {
  padding: 0.5rem;
  width: 2.5rem;
  height: 2.5rem;
}

.base-btn--primary {
  background: linear-gradient(135deg, #D8670C 0%, #c05a0a 100%);
  color: #fff;
  border: none;
  box-shadow:
    0 4px 6px -1px rgba(216, 103, 12, 0.3),
    0 2px 4px -1px rgba(216, 103, 12, 0.15);
  position: relative;
  overflow: hidden;
}

.base-btn--primary::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.18) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  animation: base-btn-shine 3.5s infinite;
  pointer-events: none;
}

@keyframes base-btn-shine {
  0%   { left: -100%; }
  20%  { left: 100%; }
  100% { left: 100%; }
}

.base-btn--primary:hover:not(:disabled):not(.base-btn--loading) {
  background: linear-gradient(135deg, #c05a0a 0%, #a24a08 100%);
  box-shadow:
    0 4px 8px -1px rgba(216, 103, 12, 0.4),
    0 2px 4px -1px rgba(216, 103, 12, 0.2);
}

.base-btn--secondary {
  background-color: #f3f4f6;
  color: #374151;
  border: 1px solid transparent;
}

.base-btn--secondary:hover:not(:disabled):not(.base-btn--loading) {
  background-color: #e5e7eb;
}

.base-btn--danger {
  background-color: #fef2f2;
  color: #b91c1c;
}

.base-btn--danger:hover:not(:disabled):not(.base-btn--loading) {
  background-color: #fee2e2;
}

.base-btn--success {
  background-color: #dcfce7;
  color: #15803d;
}

.base-btn--success:hover:not(:disabled):not(.base-btn--loading) {
  background-color: #bbf7d0;
}

.base-btn--info {
  background-color: #dbeafe;
  color: #1d4ed8;
}

.base-btn--info:hover:not(:disabled):not(.base-btn--loading) {
  background-color: #bfdbfe;
}

.base-btn--ghost {
  background-color: transparent;
  color: #374151;
  border: 1px solid #d1d5db;
}

.base-btn--ghost:hover:not(:disabled):not(.base-btn--loading) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.base-btn--text {
  background-color: transparent;
  color: #6b7280;
  padding-left: 0.25rem;
  padding-right: 0.25rem;
}

.base-btn--text:hover:not(:disabled):not(.base-btn--loading) {
  color: #374151;
}

.base-btn:disabled,
.base-btn--loading {
  opacity: 0.55;
  cursor: not-allowed;
  pointer-events: none;
}

.base-btn--block {
  width: 100%;
}

.base-btn__spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: base-btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes base-btn-spin {
  to { transform: rotate(360deg); }
}

.base-btn__icon {
  flex-shrink: 0;
  font-size: 0.9em;
}
</style>
